import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { platforms } from '@/mocks/platforms';
import { platformRatings } from '@/mocks/platforms';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    const fallbackArticle = articles[0];
    return (
      <>
        <SEOHead
          title={fallbackArticle.title}
          description={fallbackArticle.excerpt}
          keywords="欧美无码,欧美无码平台评测,欧美无码资源推荐"
          canonicalUrl={`https://yourdomain.com/article/${fallbackArticle.slug}`}
          ogType="article"
        />
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 px-4 md:px-6 pb-16">
            <div className="max-w-7xl mx-auto text-center py-20">
              <h1 className="text-2xl font-heading font-bold text-foreground-800">文章未找到</h1>
              <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline cursor-pointer">返回首页</Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  const relatedPlatformsData = article.relatedPlatforms
    .map((id) => platforms.find((p) => p.id === id))
    .filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yourdomain.com/' },
      { '@type': 'ListItem', position: 2, name: '使用指南', item: 'https://yourdomain.com/category/guide' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 欧美无码正版平台推荐指南`}
        description={article.excerpt}
        keywords={`欧美无码,${article.tags.join(',')},欧美无码资源推荐`}
        canonicalUrl={`https://yourdomain.com/article/${article.slug}`}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 px-4 md:px-6 pb-16">
          <article className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '使用指南', path: '/category/guide' },
                { label: article.title },
              ]}
            />

            <div className="mt-8 mb-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-secondary-100/30 text-secondary-400 border border-secondary-200/20 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-800 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex items-center gap-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-80 md:h-96 object-cover"
              />
            </div>

            <div className="prose-custom text-foreground-600 leading-relaxed">
              {article.content.split('\n').map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return <br key={i} />;
                if (trimmed.startsWith('## ')) {
                  return (
                    <h2 key={i} className="text-xl md:text-2xl font-heading font-bold text-foreground-800 mt-10 mb-4">
                      {trimmed.replace('## ', '')}
                    </h2>
                  );
                }
                if (trimmed.startsWith('### ')) {
                  return (
                    <h3 key={i} className="text-lg md:text-xl font-heading font-semibold text-foreground-700 mt-8 mb-3">
                      {trimmed.replace('### ', '')}
                    </h3>
                  );
                }
                if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                  return (
                    <h4 key={i} className="text-base font-semibold text-foreground-700 mt-6 mb-2">
                      {trimmed.replace(/\*\*/g, '')}
                    </h4>
                  );
                }
                if (trimmed.startsWith('- ')) {
                  return (
                    <li key={i} className="ml-4 text-sm text-foreground-500 mb-1 list-disc">
                      {trimmed.replace('- ', '')}
                    </li>
                  );
                }
                if (trimmed.startsWith('1. ') || trimmed.startsWith('2. ') || trimmed.startsWith('3. ') || trimmed.startsWith('4. ') || trimmed.startsWith('5. ')) {
                  return (
                    <li key={i} className="ml-4 text-sm text-foreground-500 mb-1 list-decimal">
                      {trimmed.replace(/^\d+\.\s*/, '')}
                    </li>
                  );
                }
                if (trimmed.startsWith('*') && trimmed.endsWith('*') && !trimmed.startsWith('**')) {
                  return (
                    <p key={i} className="text-xs text-foreground-400 italic mt-4">
                      {trimmed.replace(/\*/g, '')}
                    </p>
                  );
                }
                return (
                  <p key={i} className="text-sm text-foreground-500 mb-3 leading-relaxed">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            {relatedPlatformsData.length > 0 && (
              <section className="mt-14 pt-10 border-t border-background-200/50">
                <h3 className="text-lg font-heading font-semibold text-foreground-800 mb-5">
                  文中提到的平台
                </h3>
                <div className="flex flex-wrap gap-3">
                  {relatedPlatformsData.map((p) => {
                    if (!p) return null;
                    const ratings = platformRatings[p.id];
                    return (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background-100 border border-background-200/50"
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold"
                          style={{ backgroundColor: `${p.color}20`, color: p.color }}
                        >
                          {p.name.charAt(0)}
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-foreground-700">{p.name}</span>
                          <span className="text-xs text-primary-500 ml-2">{p.rating}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </article>

          <section className="max-w-7xl mx-auto mt-16 pt-10 border-t border-background-200/50">
            <h2 className="text-xl font-heading font-semibold text-foreground-800 mb-6">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.slug} {...a} />
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}